import { captionLineBreakChanged } from '@src/editor/services/karaokeSegmentation';
import { AspectRatio } from '@src/types.ts/video';
import { makeAutoObservable } from 'mobx';

import { DEFAULT_KARAOKE_CONFIG } from './karaokeConstants';

import type { KaraokeConfig } from './karaokeTypes';
import type { EditorStore } from '@src/editor/EditorStore';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';

/**
 * Legacy karaoke config/positioning helper. The karaoke *generation* engine was
 * removed once `ProduceKaraokeCommand` (editor layer) fully superseded it; only
 * the config accessors + aspect-ratio defaults + variable-positioning helpers
 * remain, consumed by `AIKaraokeTextProducer`, `TextSettingsUI`, and the save
 * path.
 */
export default class KaraokeProducer {
  private videoCreator: VideoCreatorStore;
  private editorStore: EditorStore;
  private karaokeConfig: KaraokeConfig = DEFAULT_KARAOKE_CONFIG;

  constructor(videoCreator: VideoCreatorStore) {
    makeAutoObservable<KaraokeProducer, 'videoCreator' | 'editorStore'>(this, {
      videoCreator: false,
      editorStore: false,
    });
    this.editorStore = videoCreator.getEditorStore();
    this.videoCreator = videoCreator;
  }

  getKaraokeConfig() {
    return this.karaokeConfig;
  }

  getKaraokeTextSettingByAspectRatio(
    aspectRatio: AspectRatio = this.videoCreator.currentVideo?.aspectRatio ||
      this.videoCreator.getDefaultAspectRatio(),
  ) {
    switch (aspectRatio) {
      case AspectRatio.AR_16_9:
        return {
          font_size: '36',
          y: '86%',
          width: '76%',
        };
      case AspectRatio.AR_1_1:
        return {
          font_size: '30',
          y: '84%',
          width: '76%',
        };
      case AspectRatio.AR_9_16:
        return {
          font_size: '24',
          y: '75%',
          width: '82%',
        };
      default:
        return {
          font_size: '36',
          y: '86%',
          width: '76%',
        };
    }
  }

  setConfig(config: Partial<KaraokeConfig>) {
    this.karaokeConfig = {
      ...DEFAULT_KARAOKE_CONFIG,
      ...this.getKaraokeTextSettingByAspectRatio(),
      ...config,
    };
  }

  async rerenderWithNewConfig(newConfig: Partial<KaraokeConfig>) {
    const editor = this.editorStore.editor;
    const currentDefaultConfig = editor.getDefaultKaraokeConfig();
    if (!currentDefaultConfig) return;

    // Merge new config over the editor's current config, but always preserve
    // the editor config's id so commandUpdateKaraokeConfig can look it up
    const fullConfig = {
      ...currentDefaultConfig,
      ...newConfig,
      id: currentDefaultConfig.id,
    };
    const lineBreakChanged = captionLineBreakChanged(
      currentDefaultConfig,
      fullConfig,
    );

    // Update the config on existing clips
    editor.commandUpdateKaraokeConfig(fullConfig, false);

    // If line break config changed, re-segment the karaoke track
    if (lineBreakChanged) {
      editor.commandProduceKaraoke({
        replaceTrack: true,
        createUndoRedoEntry: false,
      });
    }

    // Explicitly sync the karaoke store so the UI reflects the new segmentation
    this.editorStore.refreshKaraoke();
  }

  async produceKaraoke(
    _config?: KaraokeConfig,
    _addBreaksForRange?: { fromIndex: number; toIndex: number },
  ) {
    // todo: remove
  }
}
